<template>
  <div class="relative w-full bg-gradient-to-b from-black via-[#16022e] to-[#0c0019] text-white pt-20 overflow-hidden font-sans flex flex-col items-center">
    <!-- Perfect Logo Grid Pattern (No stretching) -->
    <div class="absolute inset-0 pointer-events-none select-none overflow-hidden opacity-[0.03] mix-blend-luminosity py-10">
      <div v-for="i in 10" :key="'row-'+i" class="flex justify-around items-center w-full mb-20">
        <img v-for="j in 8" :key="'col-'+j" :src="logo" class="h-16 md:h-20 w-auto object-contain" />
      </div>
    </div>

    <!-- 1. ABOUT SECTION -->
    <div v-if="showAbout && (config.title || config.description || aboutImage)" class="relative z-10 w-full max-w-6xl mx-auto px-6 flex flex-col lg:flex-row items-center justify-between gap-16 lg:gap-24 mb-32">
      
      <!-- Polaroid Photo -->
      <div v-if="aboutImage" class="relative w-full max-w-2xl lg:w-1/2 flex justify-center">
        <img :src="aboutImage" alt="Mito Games Team" class="w-full h-auto object-cover" />
      </div>

      <!-- About Text -->
      <div class="w-full lg:w-1/2 text-center lg:text-center space-y-6">
        <h2 v-if="aboutTitle" class="text-3xl lg:text-4xl font-black tracking-wide" v-html="aboutTitle"></h2>
        <p v-if="config.description" class="text-gray-300 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
          {{ config.description }}
        </p>
      </div>
    </div>

    <!-- 2. NEWSLETTER SECTION -->
    <div class="relative z-10 w-full max-w-4xl mx-auto px-6 mb-32">
      <div class="bg-[#40128B] rounded-[2rem] p-10 md:p-14 text-center shadow-2xl border border-white/5 relative overflow-hidden">
        <!-- Optional faint pattern inside newsletter -->
        <div class="absolute inset-0 bg-black/10 mix-blend-overlay"></div>
        
        <div class="relative z-10 space-y-6">
          <h2 class="text-3xl md:text-4xl font-black">
            <span class="text-[#f5a623]">Mito</span> News
          </h2>
          <p class="text-gray-200 text-lg md:text-xl max-w-2xl mx-auto">
            {{ activeTrans.newsletterDesc }}
          </p>
          
          <form @submit.prevent class="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 max-w-2xl mx-auto">
            <input 
              type="email" 
              :placeholder="activeTrans.newsletterPlaceholder" 
              class="w-full sm:flex-1 bg-[#c5a9f5] text-[#40128B] placeholder-[#8155cd] font-semibold text-lg px-6 py-4 rounded-2xl outline-none focus:ring-4 focus:ring-white/20 transition-all"
            />
            <button type="submit" class="w-full sm:w-auto bg-[#a855f7] hover:bg-[#9333ea] text-white font-black text-lg px-10 py-4 rounded-2xl shadow-lg hover:shadow-purple-500/50 transition-all transform hover:-translate-y-1">
              {{ activeTrans.newsletterButton }}
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- 3. CONTACT SECTION -->
    <div class="relative z-10 w-full text-center space-y-4 flex flex-col items-center justify-center" :class="config.contactImage ? 'mb-0' : 'mb-24'">
      <h2 class="text-4xl md:text-5xl font-black tracking-wider px-6">{{ activeTrans.contactTitle }}</h2>
      <p class="text-gray-300 text-lg md:text-xl px-6">
        {{ activeTrans.contactDesc }} <br class="md:hidden" />
        <a href="mailto:contato@mitogames.com.br" class="font-bold text-white hover:text-[#f5a623] transition-colors">contato@mitogames.com.br</a>
      </p>
      <div v-if="config.contactImage" class="mt-8 w-full transform transition-transform hover:scale-[1.01] duration-300 block mb-0 leading-none">
        <img :src="config.contactImage" class="w-full h-auto object-cover block" alt="Contact Footer Brand Logo" />
      </div>
    </div>

    <!-- 5. NEW FOOTER -->
    <footer class="relative w-full z-30 flex flex-col mt-auto">
      
      <!-- Part 1: Purple Banner -->
      <div class="relative w-full bg-[#60269E] py-8 px-6 overflow-hidden">
        <!-- Dotted Pattern Overlay -->
        <div class="absolute inset-0 opacity-[0.15] pointer-events-none" style="background-image: radial-gradient(#000 2.5px, transparent 2.5px); background-size: 14px 14px;"></div>
        
        <div class="relative z-10 w-full max-w-[1400px] mx-auto flex flex-col lg:flex-row items-center justify-between gap-8">
          <!-- Logo -->
          <div class="flex items-center gap-4">
            <img :src="logo" alt="Mito Games" class="h-16 md:h-32 w-auto object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,0.3)]" />

          </div>

          <!-- Social Icons -->
          <div class="flex flex-col items-center lg:items-end gap-3">
            <span class="text-white font-bold text-base md:text-lg tracking-wide">{{ footerSocialTitle || activeTrans.stayConnected }}</span>
            <div v-if="socialLinks && Object.keys(socialLinks).length > 0" class="flex flex-wrap items-center justify-center gap-4">
              <template v-for="(url, platform) in socialLinks" :key="platform">
                <a 
                  v-if="url && url.trim()" 
                  :href="url" 
                  target="_blank" 
                  class="w-8 h-8 text-white hover:text-[#f5a623] transition-colors flex items-center justify-center"
                  :title="platform"
                >
                  <template v-if="socialIcons && socialIcons[platform]">
                    <img :src="socialIcons[platform]" class="w-6 h-6 object-contain brightness-0 invert" />
                  </template>
                  <template v-else>
                    <svg class="w-6 h-6 fill-current" :viewBox="getSocialIcon(platform).viewBox">
                      <path :d="getSocialIcon(platform).path" />
                    </svg>
                  </template>
                </a>
              </template>
            </div>
            <div v-else class="flex flex-wrap items-center justify-center gap-4">
              <!-- Default Fallback list if config has no links -->
              <a href="#" class="w-8 h-8 text-white hover:text-[#f5a623] transition-colors"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg></a>
              <a href="#" class="w-8 h-8 text-white hover:text-[#f5a623] transition-colors"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/></svg></a>
              <a href="#" class="w-8 h-8 text-white hover:text-[#f5a623] transition-colors"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.21 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/></svg></a>
              <a href="#" class="w-8 h-8 text-white hover:text-[#f5a623] transition-colors"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg></a>
              <a href="#" class="w-8 h-8 text-white hover:text-[#f5a623] transition-colors"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.12-3.44-3.37-3.4-5.74.06-2.22 1.34-4.27 3.23-5.22 1.25-.63 2.69-.88 4.1-.71V15c-.47-.02-.95.03-1.4.15-.65.17-1.24.63-1.57 1.21-.3.51-.43 1.12-.35 1.71.09.7.46 1.34 1.01 1.76.62.48 1.44.66 2.22.56.74-.08 1.42-.48 1.87-1.07.41-.53.61-1.2.6-1.87.01-6.47.01-12.93 0-19.39l-.02-.03z"/></svg></a>
              <a href="#" class="w-8 h-8 text-white hover:text-[#f5a623] transition-colors"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg></a>
              <a href="#" class="w-8 h-8 text-white hover:text-[#f5a623] transition-colors"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg></a>
            </div>
          </div>
        </div>
      </div>

      <!-- Part 2: Black Links Area -->
      <div class="relative w-full bg-black py-16 px-6 overflow-hidden">
        <!-- Perfect Logo Grid Pattern -->
        <div class="absolute inset-0 pointer-events-none select-none overflow-hidden opacity-[0.04] mix-blend-luminosity py-10">
          <div v-for="i in 5" :key="'foot-row-'+i" class="flex justify-around items-center w-full mb-20">
            <img v-for="j in 8" :key="'foot-col-'+j" :src="logo" class="h-16 md:h-20 w-auto object-contain" />
          </div>
        </div>
        
        <div class="relative z-10 w-full max-w-5xl mx-auto flex flex-col md:flex-row items-start gap-12 md:gap-32 pl-4 md:pl-10">
          
          <!-- Column 1: Games -->
          <div class="flex flex-col space-y-3">
            <h4 class="text-white text-lg font-bold mb-1">{{ activeTrans.gamesCol }}</h4>
            <a href="#" class="text-gray-400 hover:text-white transition-colors text-sm font-medium tracking-wide">Tupi - The Legend of Araribóia</a>
            <a href="#" class="text-gray-400 hover:text-white transition-colors text-sm font-medium tracking-wide">Box Dungeons</a>
            <a href="#" class="text-gray-400 hover:text-white transition-colors text-sm font-medium tracking-wide">4 The Elements</a>
            <a href="#" class="text-gray-400 hover:text-white transition-colors text-sm font-medium tracking-wide">War Dogs</a>
            <a href="#" class="text-gray-400 hover:text-white transition-colors text-sm font-medium tracking-wide">Willy's Wonderland</a>
          </div>

          <!-- Column 2: Navigation -->
          <div class="flex flex-col space-y-3">
            <h4 class="text-white text-lg font-bold mb-1">{{ activeTrans.navCol }}</h4>
            <a href="#" class="text-gray-400 hover:text-white transition-colors text-sm font-medium tracking-wide">{{ activeTrans.aboutUs }}</a>
            <a href="#" class="text-gray-400 hover:text-white transition-colors text-sm font-medium tracking-wide">{{ activeTrans.pressKit }}</a>
            <a href="#" class="text-gray-400 hover:text-white transition-colors text-sm font-medium tracking-wide">{{ activeTrans.contact }}</a>
            <a href="#" class="text-gray-400 hover:text-white transition-colors text-sm font-medium tracking-wide">{{ activeTrans.newsletter }}</a>
          </div>

        </div>

        <!-- Scroll to Top Button -->
        <div class="relative z-10 flex justify-center mt-16 md:mt-20">
          <button @click="scrollToTop" class="bg-[#f5a623] hover:bg-[#e0961b] w-12 h-12 flex items-center justify-center transition-transform hover:-translate-y-1 rounded-sm shadow-lg cursor-pointer">
            <svg class="w-8 h-8 text-white font-black" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="3" stroke-linecap="square"><path d="M5 15l7-7 7 7"></path></svg>
          </button>
        </div>
      </div>

      <!-- Part 3: Copyright Strip -->
      <div class="relative z-10 w-full bg-[#f5a623] py-2 md:py-3 text-center">
        <p class="text-white text-xs md:text-[13px] font-medium tracking-wider">
          © 2026 <span class="font-bold">Mito Games</span>. {{ activeTrans.rightsReserved }}
        </p>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import logo from '@/assets/logo.png';
import teamPhoto from '@/assets/new1.jpg';

const translations = {
  pt: {
    newsletterDesc: 'Cadastre-se na nossa newsletter para receber atualizações! Faça parte da nossa comunidade e receba notícias e novidades sobre os nossos jogos.',
    newsletterPlaceholder: 'Digite seu e-mail aqui',
    newsletterButton: 'Cadastrar!',
    contactTitle: 'CONTATO',
    contactDesc: 'Envie uma mensagem para',
    stayConnected: 'Fique conectado conosco!',
    gamesCol: 'Jogos',
    navCol: 'Navegação',
    aboutUs: 'Sobre Nós',
    pressKit: 'Press Kit',
    contact: 'Contato',
    newsletter: 'Newsletter',
    rightsReserved: 'Todos os direitos reservados.'
  },
  en: {
    newsletterDesc: 'Join our mailing list to get updates! Be a part of our amazing community and receive news and offers about our games.',
    newsletterPlaceholder: 'Add your email here',
    newsletterButton: 'Join!',
    contactTitle: 'CONTACT',
    contactDesc: 'Send us a message at',
    stayConnected: 'Stay connected with us!',
    gamesCol: 'Games',
    navCol: 'Navigation',
    aboutUs: 'About Us',
    pressKit: 'Press Kit',
    contact: 'Contact',
    newsletter: 'News Letter',
    rightsReserved: 'All rights reserved.'
  }
};

const props = defineProps({
  language: {
    type: String,
    default: 'pt'
  },
  showAbout: {
    type: Boolean,
    default: true
  },
  config: {
    type: Object,
    default: () => ({
      title: '',
      description: "",
      image: '',
      contactImage: ''
    })
  },
  socialLinks: {
    type: Object,
    default: () => ({})
  },
  socialIcons: {
    type: Object,
    default: () => ({})
  },
  footerSocialTitle: {
    type: String,
    default: ''
  }
});

const getSocialIcon = (platform) => {
  const clean = platform.toLowerCase().trim();
  if (clean === 'instagram') {
    return {
      viewBox: '0 0 24 24',
      path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.051.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z'
    };
  }
  if (clean === 'x' || clean === 'twitter') {
    return {
      viewBox: '0 0 24 24',
      path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z'
    };
  }
  if (clean === 'discord') {
    return {
      viewBox: '0 0 24 24',
      path: 'M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 .074.074 0 0 0-.062-.05c-.163-.393-.406-.874-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.5 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z'
    };
  }
  if (clean === 'youtube') {
    return {
      viewBox: '0 0 24 24',
      path: 'M23.498 6.163a3.003 3.003 0 0 0-2.11-2.107C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.388.511a3.003 3.003 0 0 0-2.11 2.107C0 8.021 0 12 0 12s0 3.979.502 5.837a3.003 3.003 0 0 0 2.11 2.107c1.883.511 9.388.511 9.388.511s7.505 0 9.388-.511a3.003 3.003 0 0 0 2.11-2.107C24 15.979 24 12 24 12s0-3.979-.502-5.837zM9.545 15.568V8.432L15.818 12l-6.273 3.568z'
    };
  }
  return {
    viewBox: '0 0 24 24',
    path: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.13 0 2-.87 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z'
  };
};

const aboutTitle = computed(() => {
  const title = props.config?.title;
  if (!title) return '';
  return title.replace('Mito Games', '<span class="text-[#f5a623]">Mito Games</span>');
});

const aboutImage = computed(() => {
  const img = props.config?.image;
  if (!img) return '';
  
  if (Array.isArray(img)) {
    return img[0] || '';
  }
  
  return img;
});

const activeTrans = computed(() => translations[props.language] || translations.pt);

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};
</script>
